/**
 * WebrtcProvider — Manages the SIP client lifecycle, mic permissions,
 * and an audio element pool for remote streams (up to 4 concurrent sessions).
 *
 * Mounted at the app root (inside FullLayout), gated behind REACT_APP_WEBRTC_ENABLED.
 * Automatically registers using the user's phone_ext from their profile (via RolesProvider).
 *
 * Audio routing (multi-session):
 * - Each concurrent call session gets a dedicated <audio> element from AudioElementPool (max 4).
 * - When a call is held: its audio element's srcObject is set to null (Req 7.2).
 * - When a call resumes: the remote MediaStream is reattached within 500ms (Req 7.3).
 * - On autoplay failure: srcObject is retained, playback is reattempted on next user interaction (Req 7.6).
 * - During conference mode: all individual elements are detached; audio routes through AudioMixer.
 */
import React, { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { useDispatchWrapper } from "../../hooks";
import { fetchWebrtcPublicConfig, fetchWebrtcConfig } from "../../redux/webrtc/webrtcThunk";
import {
  setSipStatus,
  setCurrentCall,
  setCallProgress,
  setIceState,
  setIncomingCall,
  setRegistrationError,
  setMicPermission,
  setTransferStatus,
  setConsultCallInfo,
  setTransferError,
  resetWebrtcState,
  // Multi-call session management
  addSession,
  removeSession,
  updateSession,
  setActiveSessionId,
  setSwapInProgress,
  setMultiCallError,
  setConference,
  endConference,
  setWaitingCallId,
} from "../../redux/webrtc/webrtcSlice";
import { sipClient, SipClientStatus, SipCallInfo } from "../../services/webrtc/sipClient";
import { ManagedSession } from "../../services/webrtc/SessionManager";
import { CallSessionInfo, ConferenceState } from "../../redux/webrtc/webrtc.types";
import { AudioElementPoolImpl } from "../../services/webrtc/AudioElementPool";
import { useRoles } from "../../security/RolesProvider/RolesProvider";
import { tabSyncService } from "../../services/webrtc/TabSyncService";

// Feature flag — default OFF
export const WEBRTC_ENABLED =
  process.env.REACT_APP_WEBRTC_ENABLED === "true";

interface WebrtcProviderProps {
  children: React.ReactNode;
}

export const WebrtcProvider: React.FC<WebrtcProviderProps> = ({ children }) => {
  const dispatch = useDispatchWrapper();
  const consultAudioRef = useRef<HTMLAudioElement>(null);
  const registeredRef = useRef(false);

  // Audio element pool — replaces the single remoteAudioRef for multi-session support.
  // Each concurrent call session gets a dedicated audio element (max 4).
  const audioPoolRef = useRef<AudioElementPoolImpl>(new AudioElementPoolImpl());

  // Conference output audio element — used to play the AudioMixer's combined
  // output stream during an active conference (Req 7.4).
  const conferenceAudioRef = useRef<HTMLAudioElement | null>(null);

  const { publicConfig } = useSelector(
    (state: IStoreState) => state.webrtc
  );

  // Get user's phone_ext from their profile (loaded by RolesProvider on login)
  const { phoneExt } = useRoles();

  // On mount — fetch public config + set up audio element
  useEffect(() => {
    if (!WEBRTC_ENABLED) return;

    // Initialize cross-tab sync for call state
    tabSyncService.init();

    dispatch(fetchWebrtcPublicConfig());

    // Set up the audio pool for multi-session audio routing.
    // Also create a standalone fallback audio element for the sipClient's legacy
    // single-call path (sipClient.ontrack → remoteAudioEl). This is NOT part of
    // the pool — it's the backward-compatible element that ensures the existing
    // single-call flow works unchanged.
    const legacyAudioEl = document.createElement("audio");
    legacyAudioEl.id = "remote-audio";
    legacyAudioEl.autoplay = true;
    legacyAudioEl.setAttribute("playsinline", "");
    legacyAudioEl.style.position = "absolute";
    legacyAudioEl.style.top = "-9999px";
    legacyAudioEl.style.left = "-9999px";
    document.body.appendChild(legacyAudioEl);
    sipClient.setRemoteAudioElement(legacyAudioEl);

    if (consultAudioRef.current) {
      sipClient.setConsultAudioElement(consultAudioRef.current);
    }

    // Bind SIP events to Redux
    const handleStatusChanged = (status: SipClientStatus) => {
      dispatch(setSipStatus(status));
      tabSyncService.updateState({ isRegistered: status === "registered" });
    };
    const handleIncomingCall = (call: SipCallInfo) => {
      dispatch(setIncomingCall(call));
      dispatch(setCallProgress("ringing"));
      // Broadcast to other tabs
      tabSyncService.updateState({
        incomingCall: { remoteIdentity: call.remoteIdentity, displayName: call.displayName || "" },
        callProgress: "ringing",
      });
    };
    const handleCalling = () => {
      dispatch(setCallProgress("calling"));
      tabSyncService.updateState({ callProgress: "calling", hasActiveCall: true });
    };
    const handleCallAccepted = (call: SipCallInfo) => {
      dispatch(setCurrentCall(call));
      dispatch(setIncomingCall(null));
      dispatch(setCallProgress("connecting"));
      // Notify other tabs that call was answered on this tab
      tabSyncService.broadcastCallAnswered();
      tabSyncService.updateState({
        incomingCall: null,
        callProgress: "connecting",
        hasActiveCall: true,
        activeCall: {
          remoteIdentity: call.remoteIdentity,
          displayName: call.displayName || "",
          startTime: call.startTime || Date.now(),
          isOnHold: false,
        },
      });
    };
    const handleCallConfirmed = () => {
      dispatch(setCallProgress("connected"));
      const callInfo = sipClient.getCurrentCall();
      tabSyncService.updateState({
        callProgress: "connected",
        hasActiveCall: true,
        activeCall: callInfo
          ? {
              remoteIdentity: callInfo.remoteIdentity,
              displayName: callInfo.displayName || "",
              startTime: callInfo.startTime || Date.now(),
              isOnHold: false,
            }
          : null,
      });
    };
    const handleCallEnded = () => {
      // Only reset to idle if no sessions remain in SessionManager.
      // The handleSessionRemoved handler already derives the correct state
      // for remaining sessions. This handler fires for backward compat
      // with single-call flows.
      const sessionManager = sipClient.getSessionManager();
      if (sessionManager.getSessionCount() === 0) {
        dispatch(setCurrentCall(null));
        dispatch(setIncomingCall(null));
        dispatch(setCallProgress("idle"));
        dispatch(setIceState(null));
        tabSyncService.updateState({
          incomingCall: null,
          callProgress: "idle",
          hasActiveCall: false,
          activeCall: null,
          sessionCount: 0,
        });
      }
    };
    const handleCallFailed = () => {
      const sessionManager = sipClient.getSessionManager();
      if (sessionManager.getSessionCount() === 0) {
        dispatch(setCurrentCall(null));
        dispatch(setIncomingCall(null));
        dispatch(setCallProgress("failed"));
        dispatch(setIceState(null));
        tabSyncService.updateState({
          incomingCall: null,
          callProgress: "failed",
          hasActiveCall: false,
          activeCall: null,
          sessionCount: 0,
        });
      }
    };
    const handleRegistrationFailed = (cause: string) => {
      dispatch(setRegistrationError(cause));
    };
    const handleIceStateChanged = (iceState: string) => {
      dispatch(setIceState(iceState));
    };
    const handleCallHold = () => {
      dispatch(setCallProgress("on-hold"));
      // Update currentCall with hold state
      const callInfo = sipClient.getCurrentCall();
      if (callInfo) {
        dispatch(setCurrentCall(callInfo));
        tabSyncService.updateState({
          callProgress: "on-hold",
          activeCall: {
            remoteIdentity: callInfo.remoteIdentity,
            displayName: callInfo.displayName || "",
            startTime: callInfo.startTime || Date.now(),
            isOnHold: true,
          },
        });
      }
    };
    const handleCallUnhold = () => {
      dispatch(setCallProgress("connected"));
      // Update currentCall with hold state
      const callInfo = sipClient.getCurrentCall();
      if (callInfo) {
        dispatch(setCurrentCall(callInfo));
        tabSyncService.updateState({
          callProgress: "connected",
          activeCall: {
            remoteIdentity: callInfo.remoteIdentity,
            displayName: callInfo.displayName || "",
            startTime: callInfo.startTime || Date.now(),
            isOnHold: false,
          },
        });
      }
    };

    // Attended transfer (consult call) events
    const handleConsultCallStarted = () => {
      dispatch(setTransferStatus("consult-ringing"));
    };
    const handleConsultCallAccepted = (callInfo: SipCallInfo) => {
      dispatch(setTransferStatus("consult-connected"));
      dispatch(setConsultCallInfo(callInfo));
    };
    const handleConsultCallEnded = () => {
      dispatch(setConsultCallInfo(null));
      // Only reset transfer status if we were in a consult state (not after a successful transfer)
      // If transfer was completed, status will already be "completed"
    };
    const handleConsultCallFailed = (cause: string) => {
      dispatch(setConsultCallInfo(null));
      dispatch(setTransferError(`Consult call failed: ${cause}`));
    };
    const handleAttendedTransferCompleted = () => {
      dispatch(setTransferStatus("completed"));
      dispatch(setConsultCallInfo(null));
      // Primary session was terminated as part of the transfer — drop the
      // in-call UI back to idle so the softphone stops showing "on-hold".
      dispatch(setCurrentCall(null));
      dispatch(setCallProgress("idle"));
      dispatch(setIceState(null));
    };
    const handleAttendedTransferFailed = (cause: string) => {
      dispatch(setTransferError(cause || "Attended transfer failed"));
      dispatch(setConsultCallInfo(null));
    };

    // --- Multi-call SessionManager event handlers ---

    /**
     * Build a fresh remote MediaStream from a session's live RTCPeerConnection
     * receivers. Returns null if the connection or audio tracks aren't available.
     * Mirrors the working merge/conference path so resumed calls get a valid
     * stream even after unhold() renegotiates the peerconnection.
     */
    const getRemoteStreamFromSession = (managed: ManagedSession): MediaStream | null => {
      const pc: RTCPeerConnection | null | undefined = managed.session?.connection;
      if (!pc) return null;
      const stream = new MediaStream();
      pc.getReceivers().forEach((receiver: RTCRtpReceiver) => {
        if (receiver.track) {
          stream.addTrack(receiver.track);
        }
      });
      return stream.getAudioTracks().length > 0 ? stream : null;
    };

    /**
     * Converts a ManagedSession to the Redux-friendly CallSessionInfo shape.
     */
    const toCallSessionInfo = (managed: ManagedSession): CallSessionInfo => ({
      id: managed.id,
      direction: managed.direction,
      remoteIdentity: managed.remoteIdentity,
      displayName: managed.displayName,
      startTime: managed.startTime,
      holdStartTime: managed.holdStartTime,
      status: managed.status,
      isMuted: managed.isMuted,
    });

    const handleSessionAdded = (managed: ManagedSession) => {
      dispatch(addSession(toCallSessionInfo(managed)));
      // Backward compat: if this is a ringing inbound session and there's already
      // an active call, set it as the waiting call
      const sessionManager = sipClient.getSessionManager();
      const activeSession = sessionManager.getActiveSession();
      if (managed.status === "ringing" && managed.direction === "inbound" && activeSession && activeSession.id !== managed.id) {
        dispatch(setWaitingCallId(managed.id));
      }

      // Broadcast session count to other tabs
      // Only mark hasActiveCall if the session is actually connected (not ringing)
      const hasConnectedSession = sessionManager.getAllSessions().some(
        (s) => s.status === "active" || s.status === "held"
      );
      tabSyncService.updateState({
        sessionCount: sessionManager.getSessionCount(),
        hasActiveCall: hasConnectedSession,
      });

      // Allocate an audio element from the pool for this session (Req 7.1)
      try {
        const pool = audioPoolRef.current;
        pool.allocate(managed.id);
        // If the session already has a remote stream (e.g., outbound call accepted),
        // attach it immediately
        if (managed.remoteStream) {
          pool.reattach(managed.id, managed.remoteStream);
        }
      } catch (err) {
        console.warn("[WebrtcProvider] Failed to allocate audio element for session:", managed.id, err);
      }
    };

    const handleSessionRemoved = (sessionId: string) => {
      dispatch(removeSession(sessionId));
      // Backward compat: clear waiting call ID if this was the waiting call
      dispatch(setWaitingCallId(null));

      // Release the audio element from the pool (Req 7.1)
      const pool = audioPoolRef.current;
      pool.release(sessionId);

      // Derive backward-compatible dispatches from remaining SessionManager state
      const sessionManager = sipClient.getSessionManager();
      const remainingCount = sessionManager.getSessionCount();

      // Broadcast updated session count to other tabs
      tabSyncService.updateState({
        sessionCount: remainingCount,
        hasActiveCall: remainingCount > 0,
      });

      if (remainingCount === 0) {
        dispatch(setCurrentCall(null));
        dispatch(setIncomingCall(null));
        dispatch(setCallProgress("idle"));
      } else {
        // Update currentCall to reflect the new active session
        const active = sessionManager.getActiveSession();
        if (active) {
          dispatch(setActiveSessionId(active.id));
          const callInfo = sipClient.getCurrentCall();
          if (callInfo) {
            dispatch(setCurrentCall(callInfo));
          }
          // Update callProgress to reflect the remaining active session's state
          if (active.status === "active") {
            dispatch(setCallProgress("connected"));
          } else if (active.status === "held") {
            dispatch(setCallProgress("on-hold"));
          }
        }
      }
    };

    const handleSessionUpdated = (managed: ManagedSession) => {
      dispatch(updateSession({
        id: managed.id,
        changes: {
          status: managed.status,
          holdStartTime: managed.holdStartTime,
          isMuted: managed.isMuted,
          remoteIdentity: managed.remoteIdentity,
          displayName: managed.displayName,
        },
      }));

      // Audio element pool: handle hold/resume transitions (Req 7.2, 7.3)
      const pool = audioPoolRef.current;
      if (managed.status === "held") {
        // On held call: set audio element srcObject to null (Req 7.2)
        pool.detachSession(managed.id);
      } else if (managed.status === "active") {
        // On resumed call: reattach the remote MediaStream (Req 7.3).
        // Prefer a freshly-built stream from the peerconnection receivers because
        // unhold() renegotiates via re-INVITE and the previously stored stream's
        // tracks may be stale. Fall back to the stored remoteStream. A subsequent
        // "remoteStreamUpdated" event (from the new ontrack) will further correct
        // this if needed.
        const liveStream = getRemoteStreamFromSession(managed) ?? managed.remoteStream;
        if (liveStream) {
          pool.reattach(managed.id, liveStream);
        }
      }

      // Backward compat: keep currentCall in sync with active session state
      const sessionManager = sipClient.getSessionManager();
      const active = sessionManager.getActiveSession();
      if (active && active.id === managed.id) {
        const callInfo = sipClient.getCurrentCall();
        if (callInfo) {
          dispatch(setCurrentCall(callInfo));
        }
        // Update callProgress when the active session's status changes
        if (managed.status === "active") {
          dispatch(setCallProgress("connected"));
        } else if (managed.status === "held") {
          dispatch(setCallProgress("on-hold"));
        }
      }
    };

    const handleRemoteStreamUpdated = (sessionId: string, stream: MediaStream) => {
      // A remote MediaStream arrived (or was rebuilt) for a session. Bind it to
      // the session's dedicated pool element so audio plays. Skip held sessions
      // (their element must stay detached) and skip while a conference is active
      // (conference audio is routed through the mixer's dedicated element).
      const sessionManager = sipClient.getSessionManager();
      const managed = sessionManager.getSession(sessionId);
      if (!managed || managed.status === "held" || managed.status === "merging") {
        return;
      }
      const pool = audioPoolRef.current;
      pool.reattach(sessionId, stream);
    };

    const handleActiveSessionChanged = (sessionId: string | null) => {
      dispatch(setActiveSessionId(sessionId));
      // Clear waitingCallId — if the active session changed, the waiting call
      // was either accepted (became active) or the scenario no longer applies.
      const currentWaitingId = sipClient.getWaitingCallId();
      if (!currentWaitingId) {
        dispatch(setWaitingCallId(null));
      }
      // Backward compat: update currentCall and callProgress when active session changes
      if (sessionId) {
        const callInfo = sipClient.getCurrentCall();
        if (callInfo) {
          dispatch(setCurrentCall(callInfo));
        }
        // Update callProgress based on the new active session's state
        const sessionManager = sipClient.getSessionManager();
        const activeManaged = sessionManager.getSession(sessionId);
        if (activeManaged) {
          if (activeManaged.status === "active") {
            dispatch(setCallProgress("connected"));
          } else if (activeManaged.status === "held") {
            dispatch(setCallProgress("on-hold"));
          }
        }
      }
    };

    const handleSwapStarted = () => {
      dispatch(setSwapInProgress(true));
    };

    const handleSwapCompleted = () => {
      dispatch(setSwapInProgress(false));
    };

    const handleSwapFailed = (error: string) => {
      dispatch(setSwapInProgress(false));
      dispatch(setMultiCallError(error));
    };

    const handleSwapCriticalFailure = (error: string) => {
      dispatch(setSwapInProgress(false));
      dispatch(setMultiCallError(error));
    };

    const handleHoldFailed = (_sessionId: string, error: string) => {
      dispatch(setMultiCallError(error));
    };

    const handleResumeFailed = (_sessionId: string, error: string) => {
      dispatch(setMultiCallError(error));
    };

    const handleMaxSessionsReached = () => {
      dispatch(setMultiCallError("Maximum sessions reached"));
    };

    const handleConferenceStarted = (conference: ConferenceState) => {
      dispatch(setConference(conference));

      // Req 7.4: During active conference, detach all individual audio elements
      const pool = audioPoolRef.current;
      pool.detachAll();

      // Route AudioMixer's output stream to a dedicated conference audio element
      const mixer = sipClient.getAudioMixer();
      const outputStream = mixer?.getOutputStream();
      if (outputStream) {
        // Create or reuse the conference output audio element
        if (!conferenceAudioRef.current) {
          const confAudioEl = document.createElement("audio");
          confAudioEl.id = "conference-output";
          confAudioEl.autoplay = true;
          confAudioEl.setAttribute("playsinline", "");
          confAudioEl.style.position = "absolute";
          confAudioEl.style.top = "-9999px";
          confAudioEl.style.left = "-9999px";
          document.body.appendChild(confAudioEl);
          conferenceAudioRef.current = confAudioEl;
        }

        conferenceAudioRef.current.srcObject = outputStream;
        const playPromise = conferenceAudioRef.current.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch((error: Error) => {
            console.warn(
              "[WebrtcProvider] Conference audio play failed:",
              error.message
            );
          });
        }
      }
    };

    const handleConferenceUpdated = (conference: ConferenceState) => {
      dispatch(setConference(conference));
    };

    const handleConferenceEnded = () => {
      dispatch(endConference());

      // Req 7.5: Stop playback on the conference output audio element
      if (conferenceAudioRef.current) {
        conferenceAudioRef.current.pause();
        conferenceAudioRef.current.srcObject = null;
      }

      // Restore remaining sessions' audio to their individual pool elements
      // within 500ms (Req 7.5)
      const sessionManager = sipClient.getSessionManager();
      const remainingSessions = sessionManager.getAllSessions();
      const pool = audioPoolRef.current;

      for (const session of remainingSessions) {
        if (session.status === "ended" || session.status === "held") continue;
        const stream = getRemoteStreamFromSession(session) ?? session.remoteStream;
        if (stream) {
          pool.reattach(session.id, stream);
        }
      }
    };

    sipClient.on("statusChanged", handleStatusChanged);
    sipClient.on("incomingCall", handleIncomingCall);
    sipClient.on("calling", handleCalling);
    sipClient.on("callAccepted", handleCallAccepted);
    sipClient.on("callConfirmed", handleCallConfirmed);
    sipClient.on("callEnded", handleCallEnded);
    sipClient.on("callFailed", handleCallFailed);
    sipClient.on("registrationFailed", handleRegistrationFailed);
    sipClient.on("iceStateChanged", handleIceStateChanged);
    sipClient.on("callHold", handleCallHold);
    sipClient.on("callUnhold", handleCallUnhold);
    sipClient.on("consultCallStarted", handleConsultCallStarted);
    sipClient.on("consultCallAccepted", handleConsultCallAccepted);
    sipClient.on("consultCallEnded", handleConsultCallEnded);
    sipClient.on("consultCallFailed", handleConsultCallFailed);
    sipClient.on("attendedTransferCompleted", handleAttendedTransferCompleted);
    sipClient.on("attendedTransferFailed", handleAttendedTransferFailed);

    // Multi-call SessionManager events
    sipClient.on("sessionAdded", handleSessionAdded);
    sipClient.on("sessionRemoved", handleSessionRemoved);
    sipClient.on("sessionUpdated", handleSessionUpdated);
    sipClient.on("remoteStreamUpdated", handleRemoteStreamUpdated);
    sipClient.on("activeSessionChanged", handleActiveSessionChanged);
    sipClient.on("swapStarted", handleSwapStarted);
    sipClient.on("swapCompleted", handleSwapCompleted);
    sipClient.on("swapFailed", handleSwapFailed);
    sipClient.on("swapCriticalFailure", handleSwapCriticalFailure);
    sipClient.on("holdFailed", handleHoldFailed);
    sipClient.on("resumeFailed", handleResumeFailed);
    sipClient.on("maxSessionsReached", handleMaxSessionsReached);
    sipClient.on("conferenceStarted", handleConferenceStarted);
    sipClient.on("conferenceUpdated", handleConferenceUpdated);
    sipClient.on("conferenceEnded", handleConferenceEnded);

    // Cleanup on unmount / logout
    const handleBeforeUnload = () => {
      sipClient.unregister();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      sipClient.off("statusChanged", handleStatusChanged);
      sipClient.off("incomingCall", handleIncomingCall);
      sipClient.off("calling", handleCalling);
      sipClient.off("callAccepted", handleCallAccepted);
      sipClient.off("callConfirmed", handleCallConfirmed);
      sipClient.off("callEnded", handleCallEnded);
      sipClient.off("callFailed", handleCallFailed);
      sipClient.off("registrationFailed", handleRegistrationFailed);
      sipClient.off("iceStateChanged", handleIceStateChanged);
      sipClient.off("callHold", handleCallHold);
      sipClient.off("callUnhold", handleCallUnhold);
      sipClient.off("consultCallStarted", handleConsultCallStarted);
      sipClient.off("consultCallAccepted", handleConsultCallAccepted);
      sipClient.off("consultCallEnded", handleConsultCallEnded);
      sipClient.off("consultCallFailed", handleConsultCallFailed);
      sipClient.off("attendedTransferCompleted", handleAttendedTransferCompleted);
      sipClient.off("attendedTransferFailed", handleAttendedTransferFailed);
      // Multi-call SessionManager events
      sipClient.off("sessionAdded", handleSessionAdded);
      sipClient.off("sessionRemoved", handleSessionRemoved);
      sipClient.off("sessionUpdated", handleSessionUpdated);
      sipClient.off("remoteStreamUpdated", handleRemoteStreamUpdated);
      sipClient.off("activeSessionChanged", handleActiveSessionChanged);
      sipClient.off("swapStarted", handleSwapStarted);
      sipClient.off("swapCompleted", handleSwapCompleted);
      sipClient.off("swapFailed", handleSwapFailed);
      sipClient.off("swapCriticalFailure", handleSwapCriticalFailure);
      sipClient.off("holdFailed", handleHoldFailed);
      sipClient.off("resumeFailed", handleResumeFailed);
      sipClient.off("maxSessionsReached", handleMaxSessionsReached);
      sipClient.off("conferenceStarted", handleConferenceStarted);
      sipClient.off("conferenceUpdated", handleConferenceUpdated);
      sipClient.off("conferenceEnded", handleConferenceEnded);
      // Destroy audio element pool — removes all DOM elements and event listeners
      audioPoolRef.current.destroy();
      // Clean up the conference audio element if it exists
      if (conferenceAudioRef.current) {
        conferenceAudioRef.current.pause();
        conferenceAudioRef.current.srcObject = null;
        if (conferenceAudioRef.current.parentNode) {
          conferenceAudioRef.current.parentNode.removeChild(conferenceAudioRef.current);
        }
        conferenceAudioRef.current = null;
      }
      // Remove the legacy standalone audio element
      const legacyEl = document.getElementById("remote-audio");
      if (legacyEl && legacyEl.parentNode) {
        (legacyEl as HTMLAudioElement).srcObject = null;
        legacyEl.parentNode.removeChild(legacyEl);
      }
      sipClient.unregister();
      dispatch(resetWebrtcState());
      // Clean up cross-tab sync service
      tabSyncService.destroy();
    };
  }, []);

  // Subscribe to cross-tab state updates — when another tab broadcasts call state
  // changes, mirror them into this tab's Redux store so the UI stays consistent.
  useEffect(() => {
    if (!WEBRTC_ENABLED) return;

    // When another tab answers or rejects the call, dismiss ringing on this tab
    // and reject the local SIP session (PBX sent the INVITE to all tabs).
    const unsubCallAnswered = tabSyncService.onCallAnsweredRemotely(() => {
      // Reject the local incoming session if it's still ringing
      const sessionManager = sipClient.getSessionManager();
      const allSessions = sessionManager.getAllSessions();
      for (let i = 0; i < allSessions.length; i++) {
        const s = allSessions[i];
        if (s.status === "ringing" && s.direction === "inbound") {
          sipClient.rejectSession(s.id);
        }
      }
      // Clear local ringing state
      dispatch(setIncomingCall(null));
      dispatch(setCallProgress("idle"));
      dispatch(setWaitingCallId(null));
    });

    // Mirror remote tab state for passive display
    const unsubState = tabSyncService.onStateChange((remoteState) => {
      // If this tab has its own active session (not just ringing), don't override
      const sessionManager = sipClient.getSessionManager();
      const localSessions = sessionManager.getAllSessions();
      const hasLocalActiveCall = localSessions.some(
        (s) => s.status === "active" || s.status === "held"
      );
      if (hasLocalActiveCall) return;

      // If remote tab has an active call and we still show ringing, clear it
      if (remoteState.hasActiveCall || remoteState.callProgress === "connected") {
        const localIncoming = localSessions.find(
          (s) => s.status === "ringing" && s.direction === "inbound"
        );
        if (localIncoming) {
          sipClient.rejectSession(localIncoming.id);
        }
      }

      // Mirror the remote active call state so this tab shows the call in progress
      if (remoteState.activeCall && remoteState.hasActiveCall) {
        dispatch(setCurrentCall({
          id: "remote-tab-call",
          direction: "inbound",
          remoteIdentity: remoteState.activeCall.remoteIdentity,
          displayName: remoteState.activeCall.displayName,
          startTime: remoteState.activeCall.startTime,
          isOnHold: remoteState.activeCall.isOnHold,
          isMuted: false,
        }));
        dispatch(setCallProgress(remoteState.callProgress as any));
        dispatch(setIncomingCall(null));
      } else if (!remoteState.hasActiveCall && remoteState.callProgress === "idle") {
        // Remote call ended — clear mirrored state only if we don't have our own call
        if (!hasLocalActiveCall && localSessions.length === 0) {
          dispatch(setCurrentCall(null));
          dispatch(setCallProgress("idle"));
        }
      }
    });

    return () => {
      unsubCallAnswered();
      unsubState();
    };
  }, []);

  // Listen for call commands from a controlling tab.
  // Only the tab that owns the SIP session should execute these.
  useEffect(() => {
    if (!WEBRTC_ENABLED) return;

    const unsubCmd = tabSyncService.onCommand((cmd) => {
      // Only execute if this tab actually owns the SIP session
      const sessionManager = sipClient.getSessionManager();
      const hasLocalCall = sessionManager.getAllSessions().some(
        (s) => s.status === "active" || s.status === "held"
      );
      if (!hasLocalCall) return;

      switch (cmd.command) {
        case "mute":
          sipClient.mute();
          break;
        case "unmute":
          sipClient.unmute();
          break;
        case "hold":
          sipClient.hold();
          break;
        case "unhold":
          sipClient.unhold();
          break;
        case "hangup":
          sipClient.hangup();
          break;
        case "sendDTMF":
          if (cmd.data) sipClient.sendDTMF(cmd.data);
          break;
        default:
          break;
      }
    });

    return () => {
      unsubCmd();
    };
  }, []);

  // When this tab gains focus, immediately check if another tab has an active call
  // and mirror that state. This handles the scenario where user switches tabs and
  // expects to see the active call on the new tab.
  useEffect(() => {
    if (!WEBRTC_ENABLED) return;

    const handleVisibilityOrFocus = () => {
      if (!document.hasFocus()) return;

      // If this tab already owns a local call, skip
      const sessionManager = sipClient.getSessionManager();
      const localSessions = sessionManager.getAllSessions();
      const hasLocalActiveCall = localSessions.some(
        (s) => s.status === "active" || s.status === "held"
      );
      if (hasLocalActiveCall) return;

      // Check if a remote tab has an active call and mirror it
      const remoteCall = tabSyncService.getRemoteActiveCall();
      if (remoteCall) {
        dispatch(setCurrentCall({
          id: "remote-tab-call",
          direction: "inbound",
          remoteIdentity: remoteCall.remoteIdentity,
          displayName: remoteCall.displayName,
          startTime: remoteCall.startTime,
          isOnHold: remoteCall.isOnHold,
          isMuted: false,
        }));
        dispatch(setIncomingCall(null));
        // Determine callProgress from hold state
        dispatch(setCallProgress(remoteCall.isOnHold ? "on-hold" : "connected"));
      }
    };

    window.addEventListener("focus", handleVisibilityOrFocus);
    // Also run on mount in case we're already focused
    handleVisibilityOrFocus();

    return () => {
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, []);

  // Auto-register when the user's phone_ext becomes available from their profile
  useEffect(() => {
    if (!WEBRTC_ENABLED || !phoneExt || registeredRef.current) return;

    // If sipClient is already registered (e.g., component remounted), skip registration
    if (sipClient.getStatus() === "registered") {
      registeredRef.current = true;
      dispatch(setSipStatus("registered"));
      return;
    }

    const doRegister = async () => {
      // Request mic permission before registering
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        dispatch(setMicPermission("granted"));
      } catch {
        dispatch(setMicPermission("denied"));
        return; // Block registration if mic is denied
      }

      // Fetch full config (includes password) using the user's phone_ext
      const result = await dispatch(fetchWebrtcConfig(phoneExt));
      if (fetchWebrtcConfig.fulfilled.match(result)) {
        const config = result.payload.data;
        sipClient.register({
          sipUri: config.sipUri,
          password: config.password,
          displayName: config.displayName,
          wsServer: config.wsServer,
          iceServers: config.iceServers,
        });
        registeredRef.current = true;
      }
    };

    doRegister();
  }, [phoneExt]);

  if (!WEBRTC_ENABLED) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      {/* Audio element pool creates <audio> elements dynamically per session (max 4).
          The pool is managed by audioPoolRef — no static remoteAudioRef needed. */}
      {/* Separate audio element for consultative (attended transfer) call */}
      <audio ref={consultAudioRef} id="consult-audio" autoPlay playsInline style={{ position: "absolute", top: -9999, left: -9999 }} />
    </>
  );
};

/**
 * Hook to manually register a different extension (override).
 * Normally registration happens automatically via phone_ext from user profile.
 */
export const useWebrtcRegister = () => {
  const dispatch = useDispatchWrapper();

  const registerExtension = useCallback(
    async (extensionNumber: string) => {
      if (!WEBRTC_ENABLED || !extensionNumber) return;

      // Request mic permission before registering
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        dispatch(setMicPermission("granted"));
      } catch {
        dispatch(setMicPermission("denied"));
        return;
      }

      // Fetch full config (includes password)
      const result = await dispatch(fetchWebrtcConfig(extensionNumber));
      if (fetchWebrtcConfig.fulfilled.match(result)) {
        const config = result.payload.data;
        sipClient.register({
          sipUri: config.sipUri,
          password: config.password,
          displayName: config.displayName,
          wsServer: config.wsServer,
          iceServers: config.iceServers,
        });
      }
    },
    [dispatch]
  );

  return { registerExtension };
};
